import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../i18n";
import { api, API } from "../lib/apiClient";
import { logError } from "../lib/logger";
import { useAuth } from "../context/AuthContext";
import { STEPS, EXPENSE_FIELDS } from "../config/formConfig";
import { Header } from "../components/Header";
import { DateTimePicker } from "../components/DateTimePicker";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { ExportDialog } from "../components/ExportDialog";
import { toast } from "sonner";
import {
  Loader2, FileSpreadsheet, ShieldCheck, LogOut, Search, Eye, FileDown, Save, DollarSign, CalendarClock, Archive, RotateCcw, Inbox, BarChart3, Megaphone, Calculator,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend,
} from "recharts";
import { jsPDF } from "jspdf";

const STATUS_STYLES = {
  en_attente: "bg-amber-100 text-amber-800 border-amber-200",
  approuve: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejete: "bg-red-100 text-red-800 border-red-200",
  incomplet: "bg-purple-100 text-purple-800 border-purple-200",
};

function StatusBadge({ status, t }) {
  return (
    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border ${STATUS_STYLES[status] || STATUS_STYLES.en_attente}`}>
      {t(`status.${status || "en_attente"}`)}
    </span>
  );
}

// Field label lookup from config for the detail view.
const FIELD_MAP = {};
STEPS.forEach((s) => s.groups.forEach((g) => g.fields.forEach((f) => { FIELD_MAP[f.name] = f; })));

const REVENUE_FIELDS = ["bourse_etudes_annuelle", "contribution_parents", "contribution_conjoint", "autres_revenus", "autres_ressources", "revenus_annuels"];

export default function AdminDashboard() {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [previews, setPreviews] = useState({});
  const [deposit, setDeposit] = useState({ opens_at: null, closes_at: null, is_open: false, phase: "unscheduled" });
  const [depositInput, setDepositInput] = useState("");
  const [depositEndInput, setDepositEndInput] = useState("");
  const [nextInput, setNextInput] = useState("");
  const [nextLabel, setNextLabel] = useState("");
  const [nextSaving, setNextSaving] = useState(false);
  const [depositSaving, setDepositSaving] = useState(false);
  const [grantDefault, setGrantDefault] = useState(500);
  const [grantInput, setGrantInput] = useState("500");
  const [grantSaving, setGrantSaving] = useState(false);
  const [exportDialog, setExportDialog] = useState(null);
  const [view, setView] = useState("active");
  const [archiveSession, setArchiveSession] = useState("automne");
  const [archiveYear, setArchiveYear] = useState(String(new Date().getFullYear()));
  const [archiving, setArchiving] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archives, setArchives] = useState([]);
  const [selectedArchive, setSelectedArchive] = useState(null);
  const [archiveLogs, setArchiveLogs] = useState([]);
  const [statsYear, setStatsYear] = useState("all");
  const paidChartRef = useRef(null);
  const grantsChartRef = useRef(null);
  const trendChartRef = useRef(null);

  const toLocalInput = (iso) => {
    const d = new Date(iso);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };
  const plusOneMonth = (local) => {
    const d = new Date(local);
    d.setMonth(d.getMonth() + 1);
    return toLocalInput(d.toISOString());
  };

  const fetchDeposit = useCallback(async () => {
    try {
      const r = await api.get("/settings/deposit");
      setDeposit(r.data);
      if (r.data.opens_at) setDepositInput(toLocalInput(r.data.opens_at));
      if (r.data.closes_at) setDepositEndInput(toLocalInput(r.data.closes_at));
      setNextInput(r.data.next_opens_at ? toLocalInput(r.data.next_opens_at) : "");
      setNextLabel(r.data.next_label || "");
    } catch (e) {
      logError("Failed to fetch deposit settings:", e);
    }
  }, []);

  const fetchGrant = useCallback(async () => {
    try {
      const r = await api.get("/admin/settings/grant");
      setGrantDefault(r.data.default_amount);
      setGrantInput(String(r.data.default_amount));
    } catch (e) {
      logError("Failed to fetch grant settings:", e);
    }
  }, []);

  const saveGrant = async () => {
    const amount = parseFloat(grantInput);
    if (Number.isNaN(amount) || amount < 0) { toast.error(t("admin.grant.invalid")); return; }
    setGrantSaving(true);
    try {
      const r = await api.put("/admin/settings/grant", { default_amount: amount });
      setGrantDefault(r.data.default_amount);
      setGrantInput(String(r.data.default_amount));
      toast.success(t("admin.grant.saved"));
    } catch (e) {
      toast.error("Erreur.");
    } finally {
      setGrantSaving(false);
    }
  };

  const saveDeposit = async (override) => {
    setDepositSaving(true);
    try {
      let body;
      if (override) {
        body = override;
      } else {
        const opens = depositInput ? new Date(depositInput).toISOString() : null;
        const closes = depositEndInput ? new Date(depositEndInput).toISOString() : null;
        if (opens && closes && new Date(closes) <= new Date(opens)) {
          toast.error(t("admin.deposit.invalidRange"));
          return;
        }
        body = { opens_at: opens, closes_at: closes };
      }
      const r = await api.put("/admin/settings/deposit", body);
      setDeposit(r.data);
      setDepositInput(r.data.opens_at ? toLocalInput(r.data.opens_at) : "");
      setDepositEndInput(r.data.closes_at ? toLocalInput(r.data.closes_at) : "");
      toast.success(t("admin.deposit.saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur.");
    } finally {
      setDepositSaving(false);
    }
  };

  const onStartChange = (v) => {
    setDepositInput(v);
    if (v && !depositEndInput) setDepositEndInput(plusOneMonth(v));
  };

  const openNow = () => {
    const now = new Date();
    const end = new Date(now); end.setMonth(end.getMonth() + 1);
    saveDeposit({ opens_at: now.toISOString(), closes_at: end.toISOString() });
  };

  const closeNow = () => saveDeposit({ opens_at: deposit.opens_at, closes_at: new Date().toISOString() });

  const saveNext = async (clear = false) => {
    setNextSaving(true);
    try {
      const body = clear
        ? { next_opens_at: null, next_label: "" }
        : { next_opens_at: nextInput ? new Date(nextInput).toISOString() : null, next_label: nextLabel };
      const r = await api.put("/admin/settings/next-session", body);
      setDeposit(r.data);
      setNextInput(r.data.next_opens_at ? toLocalInput(r.data.next_opens_at) : "");
      setNextLabel(r.data.next_label || "");
      toast.success(t("admin.next.saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur.");
    } finally {
      setNextSaving(false);
    }
  };

  useEffect(() => {
    if (!loading && !user?.is_admin) navigate("/admin", { replace: true });
  }, [user, loading, navigate]);

  const fetchList = useCallback(async () => {
    setLoadingList(true);
    try {
      let url = "/admin/applications";
      if (view === "archives" && selectedArchive) {
        url += `?archived=true&session=${selectedArchive.session}&year=${selectedArchive.year}`;
      }
      const res = await api.get(url);
      setItems(res.data);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) navigate("/admin", { replace: true });
    } finally {
      setLoadingList(false);
    }
  }, [navigate, view, selectedArchive]);

  const fetchArchives = useCallback(async () => {
    try {
      const r = await api.get("/admin/archives");
      setArchives(r.data);
      setSelectedArchive((cur) => cur || (r.data.length ? { session: r.data[0].session, year: r.data[0].year } : null));
    } catch (e) {
      logError("Failed to fetch archives:", e);
    }
  }, []);

  const fetchArchiveLogs = useCallback(async () => {
    try {
      const r = await api.get("/admin/archive-logs");
      setArchiveLogs(r.data);
    } catch (e) {
      logError("Failed to fetch archive logs:", e);
    }
  }, []);

  const doArchive = async () => {
    setArchiving(true);
    try {
      const r = await api.post("/admin/applications/archive", { session: archiveSession, year: parseInt(archiveYear, 10) });
      toast.success(`${r.data.archived_count} ${t("admin.archive.count")}`);
      setConfirmArchive(false);
      fetchList();
      fetchArchives();
      fetchArchiveLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur.");
    } finally {
      setArchiving(false);
    }
  };

  const doUnarchive = async (code) => {
    try {
      await api.post(`/admin/applications/${code}/unarchive`);
      toast.success(t("admin.archive.unarchived"));
      fetchList();
      fetchArchives();
      fetchArchiveLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur.");
    }
  };

  useEffect(() => {
    if (user?.is_admin) { fetchList(); fetchDeposit(); fetchGrant(); fetchArchives(); fetchArchiveLogs(); }
  }, [user, fetchList, fetchDeposit, fetchGrant, fetchArchives, fetchArchiveLogs]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (statusFilter !== "all" && (it.statut_dossier || "en_attente") !== statusFilter) return false;
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return [it.code_anonyme, it.code_comite, it.nom, it.prenom, it.programme, it.faculte, it.courriel]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [items, search, statusFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    if (sortBy === "deficit_desc") arr.sort((a, b) => (b.deficit || 0) - (a.deficit || 0));
    else if (sortBy === "deficit_asc") arr.sort((a, b) => (a.deficit || 0) - (b.deficit || 0));
    return arr;
  }, [filtered, sortBy]);

  const activeBilan = useMemo(() => {
    if (view !== "active") return null;
    const grants = items.filter((i) => i.statut_dossier === "approuve").length;
    const paid = items.filter((i) => i.versement_effectue).reduce((s, i) => s + (i.bourse_accordee || 0), 0);
    const pending = items.filter((i) => (i.statut_dossier || "en_attente") === "en_attente").length;
    const incomplete = items.filter((i) => i.statut_dossier === "incomplet").length;
    return { grants, paid, pending, incomplete };
  }, [items, view]);

  const statsYears = useMemo(
    () => [...new Set(archives.map((g) => g.year).filter((y) => y != null))].sort((a, b) => b - a),
    [archives]
  );

  const statsData = useMemo(
    () => archives
      .filter((g) => statsYear === "all" || String(g.year) === String(statsYear))
      .map((g) => ({
        name: `${t(`admin.archive.session.${g.session}`)} ${g.year}`,
        paid: g.total_paid || 0,
        grants: g.grants_count || 0,
      })),
    [archives, t, statsYear]
  );

  const statsRows = useMemo(
    () => archives
      .filter((g) => statsYear === "all" || String(g.year) === String(statsYear))
      .map((g) => ({ session: g.session, year: g.year, count: g.count || 0, grants: g.grants_count || 0, paid: g.total_paid || 0 })),
    [archives, statsYear]
  );

  const trendData = useMemo(() => {
    const byYear = {};
    archives.forEach((g) => { if (g.year != null) byYear[g.year] = (byYear[g.year] || 0) + (g.total_paid || 0); });
    const years = Object.keys(byYear).map(Number).sort((a, b) => a - b);
    let cum = 0;
    return years.map((y) => { cum += byYear[y]; return { name: String(y), annuel: byYear[y], cumule: cum }; });
  }, [archives]);

  const svgToPng = (ref, scale = 2) => new Promise((resolve, reject) => {
    const svg = ref.current?.querySelector("svg");
    if (!svg) { reject(new Error("no-svg")); return; }
    const bbox = svg.getBoundingClientRect();
    const w = Math.ceil(bbox.width) || 600;
    const h = Math.ceil(bbox.height) || 300;
    const clone = svg.cloneNode(true);
    clone.setAttribute("width", w);
    clone.setAttribute("height", h);
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    const xml = new XMLSerializer().serializeToString(clone);
    const url = URL.createObjectURL(new Blob([xml], { type: "image/svg+xml;charset=utf-8" }));
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = w * scale;
      canvas.height = h * scale;
      const ctx = canvas.getContext("2d");
      ctx.scale(scale, scale);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      resolve({ dataUrl: canvas.toDataURL("image/png"), w, h });
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("img")); };
    img.src = url;
  });

  const imgUrlToDataUrl = (url) => new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve({ dataUrl: canvas.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight });
    };
    img.onerror = () => reject(new Error("logo"));
    img.src = url;
  });

  const downloadChart = (ref, filename) => {
    svgToPng(ref)
      .then(({ dataUrl }) => {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = filename;
        a.click();
      })
      .catch(() => toast.error("Export du graphique impossible."));
  };

  const exportBilanPDF = async () => {
    setExporting("pdf");
    try {
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 40;
      let y = margin;
      const fmt = (n) => (n || 0).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { style: "currency", currency: "CAD" });
      const period = statsYear === "all" ? t("admin.stats.allYears") : String(statsYear);

      const logo = await imgUrlToDataUrl("/step-logo.png").catch(() => null);
      if (logo) {
        const lw = 90;
        const lh = (logo.h / logo.w) * lw;
        doc.addImage(logo.dataUrl, "PNG", margin, y, lw, lh);
        y += lh + 12;
      }
      doc.setFontSize(18); doc.setTextColor(158, 27, 50);
      doc.text("Bilan du programme de bourse d'aide", margin, y); y += 24;
      doc.setFontSize(12); doc.setTextColor(51, 65, 85);
      doc.text(`${t("admin.stats.yearFilter")}: ${period}`, margin, y); y += 16;
      doc.setFontSize(9); doc.setTextColor(148, 163, 184);
      doc.text(new Date().toLocaleString(lang === "fr" ? "fr-CA" : "en-CA"), margin, y); y += 22;

      doc.setFontSize(11); doc.setTextColor(15, 23, 42);
      if (statsRows.length === 0) {
        doc.text(t("admin.stats.empty"), margin, y); y += 16;
      } else {
        statsRows.forEach((r) => {
          const line = `${t("admin.archive.session." + r.session)} ${r.year}  —  ${r.count} ${t("admin.archive.count")}  ·  ${r.grants} ${t("admin.archive.grants").toLowerCase()}  ·  ${fmt(r.paid)}`;
          doc.text(line, margin, y); y += 16;
        });
      }
      y += 10;

      for (const ref of [trendChartRef, paidChartRef, grantsChartRef]) {
        try {
          const { dataUrl, w, h } = await svgToPng(ref);
          const imgW = pageW - margin * 2;
          const imgH = (h / w) * imgW;
          if (y + imgH > pageH - margin) { doc.addPage(); y = margin; }
          doc.addImage(dataUrl, "PNG", margin, y, imgW, imgH);
          y += imgH + 16;
        } catch (e) { /* chart not mounted; skip */ }
      }
      const pages = doc.getNumberOfPages();
      for (let i = 1; i <= pages; i++) {
        doc.setPage(i);
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.5);
        doc.line(margin, pageH - 30, pageW - margin, pageH - 30);
        doc.setFontSize(8); doc.setTextColor(148, 163, 184);
        doc.text("STEP — Université Laval · Programme de bourse d'aide", margin, pageH - 18);
        doc.text(`${i} / ${pages}`, pageW - margin, pageH - 18, { align: "right" });
      }
      doc.save(`STEP_bilan_${period}.pdf`);
    } catch (e) {
      toast.error("Export PDF impossible.");
    } finally {
      setExporting("");
    }
  };

  const openDetail = async (code) => {
    setSelected(code);
    setDetail(null);
    try {
      const res = await api.get(`/admin/applications/${code}`);
      setDetail(res.data);
    } catch (e) {
      toast.error("Erreur de chargement.");
      setSelected(null);
    }
  };

  const saveAdmin = async () => {
    setSaving(true);
    try {
      const res = await api.patch(`/admin/applications/${selected}`, {
        statut_dossier: detail.statut_dossier,
        bourse_accordee: parseFloat(detail.bourse_accordee) || 0,
        versement_effectue: !!detail.versement_effectue,
        notes_admin: detail.notes_admin || "",
      });
      setDetail(res.data);
      toast.success(t("admin.saved"));
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur.");
    } finally {
      setSaving(false);
    }
  };

  const downloadFile = async (fileRef, fallbackName) => {
    if (!fileRef?.file_id) return;
    try {
      const res = await api.get(`/admin/files/${fileRef.file_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Téléchargement impossible.");
    }
  };

  const previewFile = async (fileRef, key) => {
    if (!fileRef?.file_id) return;
    if (previews[key]) {
      URL.revokeObjectURL(previews[key]);
      setPreviews((p) => ({ ...p, [key]: null }));
      return;
    }
    try {
      const res = await api.get(`/admin/files/${fileRef.file_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      setPreviews((p) => ({ ...p, [key]: url }));
    } catch (e) {
      toast.error("Aperçu impossible.");
    }
  };

  const exportData = async (mode, fmt = "xlsx", columns = null) => {
    setExporting(`${mode}-${fmt}`);
    try {
      const params = new URLSearchParams({ fmt });
      if (columns && columns.length) params.set("columns", columns.join(","));
      if (view === "archives" && selectedArchive) {
        params.set("archived", "true");
        params.set("session", selectedArchive.session);
        params.set("year", String(selectedArchive.year));
      }
      const res = await api.get(`/admin/export/${mode}?${params.toString()}`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      const suffix = view === "archives" && selectedArchive ? `_${selectedArchive.session}_${selectedArchive.year}` : "";
      const label = { full: "complet", anonymous: "anonyme", scoring: "scoring_comite" }[mode] || mode;
      a.download = `STEP_bourse_${label}${suffix}.${fmt}`;
      a.click();
      URL.revokeObjectURL(blobUrl);
      setExportDialog(null);
    } catch (e) {
      toast.error("Export impossible.");
    } finally {
      setExporting("");
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/admin", { replace: true });
  };

  if (loading || !user?.is_admin) {
    return <div className="min-h-screen grid place-items-center bg-background"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <Header
        showAdminLink={false}
        right={
          <div className="flex items-center gap-3">
            <span className="hidden md:flex items-center gap-2 text-sm text-slate-600">
              <ShieldCheck className="w-4 h-4 text-primary" /> {user.email}
            </span>
            <Button variant="ghost" size="sm" onClick={doLogout} data-testid="logout-btn" className="text-slate-500">
              <LogOut className="w-4 h-4 mr-1.5" /> {t("admin.logout")}
            </Button>
          </div>
        }
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900">{t("admin.title")}</h1>
            <p className="text-sm text-slate-500 mt-1" data-testid="applications-count">
              {items.length} {t("admin.total")}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button disabled={!!exporting} onClick={() => setExportDialog("full")} data-testid="export-full-btn" className="bg-secondary hover:bg-secondary/90 text-white">
              {exporting.startsWith("full") ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
              {t("admin.exportFull")}
            </Button>
            <Button disabled={!!exporting} onClick={() => setExportDialog("anonymous")} data-testid="export-anon-btn" variant="outline" className="border-primary text-primary hover:bg-primary/5">
              {exporting.startsWith("anonymous") ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              {t("admin.exportAnon")}
            </Button>
            <Button disabled={!!exporting} onClick={() => exportData("scoring", "xlsx")} data-testid="export-scoring-btn" variant="outline" className="border-secondary text-secondary hover:bg-secondary/5">
              {exporting.startsWith("scoring") ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}
              {t("admin.exportScoring")}
            </Button>
          </div>
        </div>

        <ExportDialog
          open={!!exportDialog}
          onOpenChange={(o) => { if (!o) setExportDialog(null); }}
          mode={exportDialog || "full"}
          exporting={exporting}
          onExport={(fmt, columns) => exportData(exportDialog, fmt, columns)}
        />

        <div className="flex gap-1 mb-5 border-b border-slate-200" data-testid="admin-tabs">
          <button
            onClick={() => setView("active")}
            data-testid="tab-active"
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${view === "active" ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          >
            <Inbox className="w-4 h-4" /> {t("admin.tab.active")}
          </button>
          <button
            onClick={() => setView("archives")}
            data-testid="tab-archives"
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${view === "archives" ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          >
            <Archive className="w-4 h-4" /> {t("admin.tab.archives")}
          </button>
          <button
            onClick={() => setView("stats")}
            data-testid="tab-stats"
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${view === "stats" ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          >
            <BarChart3 className="w-4 h-4" /> {t("admin.tab.stats")}
          </button>
        </div>

        {view === "active" && (
        <>
        <div className="bg-card rounded-xl shadow-sm border border-slate-200/80 p-5 mb-4" data-testid="deposit-control">
          <div className="flex items-center justify-between gap-3 mb-1">
            <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-primary" /> {t("admin.deposit.title")}
            </h3>
            <span
              data-testid="deposit-status-badge"
              className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                deposit.phase === "open" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                : deposit.phase === "upcoming" ? "bg-sky-100 text-sky-800 border-sky-200"
                : deposit.phase === "closed" ? "bg-red-100 text-red-800 border-red-200"
                : "bg-amber-100 text-amber-800 border-amber-200"}`}
            >
              {t(`admin.deposit.badge.${deposit.phase || "unscheduled"}`)}
            </span>
          </div>
          <p className="text-xs text-slate-500 mb-3" data-testid="deposit-current">
            {deposit.opens_at
              ? <>
                  {t("admin.deposit.current")} : <span className="font-semibold text-slate-700">{new Date(deposit.opens_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}</span>
                  {" · "}{t("admin.deposit.closes")} : <span className="font-semibold text-slate-700" data-testid="deposit-current-end">{deposit.closes_at ? new Date(deposit.closes_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA") : t("admin.deposit.noEnd")}</span>
                </>
              : t("admin.deposit.none")}
          </p>
          <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-slate-600">{t("admin.deposit.start")}</Label>
              <DateTimePicker
                value={depositInput}
                onChange={onStartChange}
                lang={lang}
                placeholder={t("admin.deposit.pickPlaceholder")}
                testid="deposit-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-slate-600">{t("admin.deposit.end")}</Label>
              <DateTimePicker
                value={depositEndInput}
                onChange={setDepositEndInput}
                lang={lang}
                placeholder={t("admin.deposit.pickPlaceholder")}
                testid="deposit-end-input"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => saveDeposit()} disabled={depositSaving} data-testid="deposit-save-btn" className="bg-primary hover:bg-primary/90">
                {depositSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {t("admin.deposit.save")}
              </Button>
              <Button variant="outline" onClick={openNow} disabled={depositSaving} data-testid="deposit-opennow-btn">
                {t("admin.deposit.openNow")}
              </Button>
              {deposit.phase === "open" && (
                <Button variant="outline" onClick={closeNow} disabled={depositSaving} data-testid="deposit-closenow-btn" className="text-red-700 border-red-200 hover:bg-red-50">
                  {t("admin.deposit.closeNow")}
                </Button>
              )}
              <Button variant="ghost" onClick={() => { setDepositInput(""); setDepositEndInput(""); saveDeposit({ opens_at: null, closes_at: null }); }} disabled={depositSaving} data-testid="deposit-reset-btn" className="text-slate-500">
                {t("admin.deposit.reset")}
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">{t("admin.deposit.oneMonthHint")}</p>

          <div className="mt-4 pt-4 border-t border-slate-100" data-testid="next-session-control">
            <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-2">
              <Megaphone className="w-3.5 h-3.5 text-primary" /> {t("admin.next.title")}
            </h4>
            <p className="text-xs text-slate-500 mt-1 mb-3" data-testid="next-session-current">
              {deposit.next_opens_at
                ? <>{t("admin.next.current")} : <span className="font-semibold text-slate-700">{deposit.next_label ? `${deposit.next_label} — ` : ""}{new Date(deposit.next_opens_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}</span></>
                : t("admin.next.none")}
              {" · "}{t("admin.next.hint")}
            </p>
            <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
              <div className="space-y-1 flex flex-col">
                <Label className="text-xs font-semibold text-slate-600">{t("admin.next.date")}</Label>
                <DateTimePicker
                  value={nextInput}
                  onChange={setNextInput}
                  lang={lang}
                  placeholder={t("admin.deposit.pickPlaceholder")}
                  testid="next-session-input"
                />
              </div>
              <div className="space-y-1 flex flex-col">
                <Label className="text-xs font-semibold text-slate-600">{t("admin.next.label")}</Label>
                <Input value={nextLabel} maxLength={80} onChange={(e) => setNextLabel(e.target.value)}
                  placeholder={t("admin.next.labelPlaceholder")} data-testid="next-session-label-input" className="bg-white sm:w-64" />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => saveNext(false)} disabled={nextSaving || !nextInput} data-testid="next-session-save-btn" className="bg-primary hover:bg-primary/90">
                  {nextSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Megaphone className="w-4 h-4 mr-2" />}
                  {t("admin.next.save")}
                </Button>
                {deposit.next_opens_at && (
                  <Button variant="ghost" onClick={() => saveNext(true)} disabled={nextSaving} data-testid="next-session-clear-btn" className="text-slate-500">
                    {t("admin.next.clear")}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl shadow-sm border border-slate-200/80 p-5 mb-4" data-testid="grant-control">
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-primary" /> {t("admin.grant.title")}
          </h3>
          <p className="text-xs text-slate-500 mb-3" data-testid="grant-current">
            {t("admin.grant.current")} : <span className="font-semibold text-slate-700">{(grantDefault || 0).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { style: "currency", currency: "CAD" })}</span> — {t("admin.grant.hint")}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="relative">
              <Input type="number" min={0} step={50} value={grantInput}
                data-testid="grant-input"
                onChange={(e) => setGrantInput(e.target.value)}
                className="bg-white w-40 pr-8" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
            </div>
            <Button onClick={saveGrant} disabled={grantSaving} data-testid="grant-save-btn" className="bg-primary hover:bg-primary/90">
              {grantSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {t("admin.grant.save")}
            </Button>
          </div>
        </div>

        <div className="bg-card rounded-xl shadow-sm border border-slate-200/80 p-5 mb-4" data-testid="archive-control">
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide flex items-center gap-2 mb-1">
            <Archive className="w-4 h-4 text-primary" /> {t("admin.archive.title")}
          </h3>
          <p className="text-xs text-slate-500 mb-3">{t("admin.archive.desc")}</p>
          <div className="flex flex-col sm:flex-row sm:items-end gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-600">{t("admin.archive.session")}</Label>
              <Select value={archiveSession} onValueChange={setArchiveSession}>
                <SelectTrigger className="bg-white sm:w-44" data-testid="archive-session-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="automne">{t("admin.archive.session.automne")}</SelectItem>
                  <SelectItem value="hiver">{t("admin.archive.session.hiver")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-600">{t("admin.archive.year")}</Label>
              <Input type="number" value={archiveYear} onChange={(e) => setArchiveYear(e.target.value)} data-testid="archive-year-input" className="bg-white sm:w-32" />
            </div>
            <Button onClick={() => setConfirmArchive(true)} disabled={archiving || items.length === 0} data-testid="archive-btn" className="bg-secondary hover:bg-secondary/90 text-white">
              <Archive className="w-4 h-4 mr-2" /> {t("admin.archive.btn")}
            </Button>
          </div>
          {items.length === 0 && <p className="text-xs text-slate-400 mt-2">{t("admin.archive.noneActive")}</p>}
        </div>
        </>
        )}

        {view === "archives" && (
        <div className="bg-card rounded-xl shadow-sm border border-slate-200/80 p-5 mb-4" data-testid="archives-panel">
          {archives.length === 0 ? (
            <p className="text-sm text-slate-400">{t("admin.archive.none")}</p>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-600">{t("admin.archive.select")}</Label>
                <Select
                  value={selectedArchive ? `${selectedArchive.session}|${selectedArchive.year}` : ""}
                  onValueChange={(v) => { const [s, y] = v.split("|"); setSelectedArchive({ session: s, year: parseInt(y, 10) }); }}
                >
                  <SelectTrigger className="bg-white sm:w-72" data-testid="archive-group-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {archives.map((g) => (
                      <SelectItem key={`${g.session}|${g.year}`} value={`${g.session}|${g.year}`}>
                        {t(`admin.archive.session.${g.session}`)} {g.year} ({g.count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedArchive && (() => {
                const g = archives.find((x) => x.session === selectedArchive.session && x.year === selectedArchive.year);
                if (!g) return null;
                return (
                  <div className="sm:ml-auto flex flex-col items-start sm:items-end gap-2" data-testid="archive-log">
                    <div className="flex gap-2">
                      <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-1.5">
                        <p className="text-[10px] uppercase tracking-wider text-emerald-600 font-semibold">{t("admin.archive.grants")}</p>
                        <p className="font-display font-extrabold text-emerald-700 text-sm" data-testid="archive-grants-count">{g.grants_count ?? 0}</p>
                      </div>
                      <div className="rounded-lg bg-secondary/10 border border-secondary/20 px-3 py-1.5">
                        <p className="text-[10px] uppercase tracking-wider text-secondary font-semibold">{t("admin.archive.totalPaid")}</p>
                        <p className="font-display font-extrabold text-secondary text-sm" data-testid="archive-total-paid">
                          {(g.total_paid || 0).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { style: "currency", currency: "CAD" })}
                        </p>
                      </div>
                    </div>
                    <div className="text-xs text-slate-500">
                      <span className="font-semibold text-slate-700">{g.count}</span> {t("admin.archive.groupCount")} ·{" "}
                      {t("admin.archive.archivedBy")} <span className="font-medium">{g.archived_by || "—"}</span> {t("admin.archive.on")}{" "}
                      {g.archived_at ? new Date(g.archived_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA") : "—"}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
        )}

        {view === "archives" && (
        <div className="bg-card rounded-xl shadow-sm border border-slate-200/80 p-5 mb-4" data-testid="archive-journal">
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide flex items-center gap-2 mb-3">
            <Archive className="w-4 h-4 text-primary" /> {t("admin.archive.journal")}
          </h3>
          {archiveLogs.length === 0 ? (
            <p className="text-sm text-slate-400">{t("admin.archive.journalEmpty")}</p>
          ) : (
            <ol className="space-y-2" data-testid="archive-journal-list">
              {archiveLogs.map((l, i) => (
                <li key={`${l.at || "l"}-${i}`} className="flex flex-wrap items-center justify-between gap-2 text-sm bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                  <span className="flex items-center gap-2">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border ${l.action === "archive" ? "bg-secondary/10 text-secondary border-secondary/20" : "bg-amber-100 text-amber-800 border-amber-200"}`}>
                      {l.action === "archive" ? t("admin.archive.actionArchive") : t("admin.archive.actionUnarchive")}
                    </span>
                    <span className="font-medium text-slate-700">
                      {l.session ? t(`admin.archive.session.${l.session}`) : "—"} {l.year ?? ""}
                    </span>
                    {l.code_anonyme && <span className="font-mono text-xs text-primary">{l.code_anonyme}</span>}
                    {l.action === "archive" && <span className="text-xs text-slate-500">· {l.count} {t("admin.archive.count")}</span>}
                    <span className="text-xs text-slate-400">{t("admin.history.by")} {l.by || "—"}</span>
                  </span>
                  <span className="text-xs text-slate-500">{l.at ? new Date(l.at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA") : ""}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
        )}

        {view === "active" && activeBilan && (
        <div className="bg-card rounded-xl shadow-sm border border-slate-200/80 p-5 mb-4" data-testid="active-bilan">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-primary" /> {t("admin.bilan.title")}
            </h3>
            <div className="flex flex-wrap gap-2 sm:ml-auto">
              <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-1.5">
                <p className="text-[10px] uppercase tracking-wider text-emerald-600 font-semibold">{t("admin.archive.grants")}</p>
                <p className="font-display font-extrabold text-emerald-700 text-sm" data-testid="active-grants">{activeBilan.grants}</p>
              </div>
              <div className="rounded-lg bg-secondary/10 border border-secondary/20 px-3 py-1.5">
                <p className="text-[10px] uppercase tracking-wider text-secondary font-semibold">{t("admin.archive.totalPaid")}</p>
                <p className="font-display font-extrabold text-secondary text-sm" data-testid="active-paid">
                  {activeBilan.paid.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { style: "currency", currency: "CAD" })}
                </p>
              </div>
              <div className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-1.5">
                <p className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold">{t("status.en_attente")}</p>
                <p className="font-display font-extrabold text-amber-700 text-sm" data-testid="active-pending">{activeBilan.pending}</p>
              </div>
              <div className="rounded-lg bg-purple-50 border border-purple-100 px-3 py-1.5">
                <p className="text-[10px] uppercase tracking-wider text-purple-600 font-semibold">{t("status.incomplet")}</p>
                <p className="font-display font-extrabold text-purple-700 text-sm" data-testid="active-incomplete">{activeBilan.incomplete}</p>
              </div>
            </div>
          </div>
        </div>
        )}

        {view === "stats" && (
          archives.length === 0 ? (
            <div className="bg-card rounded-xl shadow-sm border border-slate-200/80 p-10 text-center text-slate-400" data-testid="stats-empty">
              {t("admin.stats.empty")}
            </div>
          ) : (
            <div className="space-y-4" data-testid="stats-panel">
              <div className="flex flex-wrap items-center gap-3">
                <Label className="text-xs font-semibold text-slate-600">{t("admin.stats.yearFilter")}</Label>
                <Select value={statsYear} onValueChange={setStatsYear}>
                  <SelectTrigger className="w-44 bg-white" data-testid="stats-year-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("admin.stats.allYears")}</SelectItem>
                    {statsYears.map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={exportBilanPDF} disabled={exporting === "pdf"} data-testid="export-bilan-pdf" className="sm:ml-auto bg-secondary hover:bg-secondary/90 text-white">
                  {exporting === "pdf" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />}
                  {t("admin.stats.pdf")}
                </Button>
              </div>

              {trendData.length > 0 && (
                <div className="bg-card rounded-xl shadow-sm border border-slate-200/80 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-primary" /> {t("admin.stats.trendTitle")}
                    </h3>
                    <Button size="sm" variant="outline" data-testid="download-trend-chart" onClick={() => downloadChart(trendChartRef, "STEP_stats_tendance.png")}>
                      <FileDown className="w-4 h-4 mr-1.5" /> {t("admin.stats.download")}
                    </Button>
                  </div>
                  <div ref={trendChartRef} style={{ width: "100%", height: 300 }}>
                    <ResponsiveContainer>
                      <LineChart data={trendData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                        <XAxis dataKey="name" fontSize={12} stroke="#64748b" />
                        <YAxis fontSize={12} stroke="#64748b" />
                        <Tooltip formatter={(v) => v.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { style: "currency", currency: "CAD" })} />
                        <Legend />
                        <Line type="monotone" dataKey="cumule" name={t("admin.stats.cumulative")} stroke="#9E1B32" strokeWidth={3} dot={{ r: 4 }} />
                        <Line type="monotone" dataKey="annuel" name={t("admin.stats.annual")} stroke="#0f766e" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {statsData.length === 0 ? (
                <div className="bg-card rounded-xl shadow-sm border border-slate-200/80 p-10 text-center text-slate-400" data-testid="stats-empty-filtered">
                  {t("admin.stats.empty")}
                </div>
              ) : (
                <>
                  <div className="bg-card rounded-xl shadow-sm border border-slate-200/80 p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-primary" /> {t("admin.stats.paidTitle")}
                      </h3>
                      <Button size="sm" variant="outline" data-testid="download-paid-chart" onClick={() => downloadChart(paidChartRef, "STEP_stats_montant_verse.png")}>
                        <FileDown className="w-4 h-4 mr-1.5" /> {t("admin.stats.download")}
                      </Button>
                    </div>
                    <div ref={paidChartRef} style={{ width: "100%", height: 300 }}>
                      <ResponsiveContainer>
                        <BarChart data={statsData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                          <XAxis dataKey="name" fontSize={12} stroke="#64748b" />
                          <YAxis fontSize={12} stroke="#64748b" />
                          <Tooltip formatter={(v) => v.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { style: "currency", currency: "CAD" })} />
                          <Bar dataKey="paid" name={t("admin.archive.totalPaid")} fill="#9E1B32" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="bg-card rounded-xl shadow-sm border border-slate-200/80 p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-primary" /> {t("admin.stats.grantsTitle")}
                      </h3>
                      <Button size="sm" variant="outline" data-testid="download-grants-chart" onClick={() => downloadChart(grantsChartRef, "STEP_stats_bourses.png")}>
                        <FileDown className="w-4 h-4 mr-1.5" /> {t("admin.stats.download")}
                      </Button>
                    </div>
                    <div ref={grantsChartRef} style={{ width: "100%", height: 300 }}>
                      <ResponsiveContainer>
                        <BarChart data={statsData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                          <XAxis dataKey="name" fontSize={12} stroke="#64748b" />
                          <YAxis fontSize={12} stroke="#64748b" allowDecimals={false} />
                          <Tooltip />
                          <Bar dataKey="grants" name={t("admin.archive.grants")} fill="#0f766e" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </>
              )}
            </div>
          )
        )}

        {view !== "stats" && (
        <>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="admin-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("admin.search")}
              className="pl-9 bg-white"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-52 bg-white" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("admin.filter.all")}</SelectItem>
              <SelectItem value="en_attente">{t("status.en_attente")}</SelectItem>
              <SelectItem value="approuve">{t("status.approuve")}</SelectItem>
              <SelectItem value="rejete">{t("status.rejete")}</SelectItem>
              <SelectItem value="incomplet">{t("status.incomplet")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-full sm:w-56 bg-white" data-testid="sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">{t("admin.sort.recent")}</SelectItem>
              <SelectItem value="deficit_desc">{t("admin.sort.deficit_desc")}</SelectItem>
              <SelectItem value="deficit_asc">{t("admin.sort.deficit_asc")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="bg-card rounded-xl shadow-sm border border-slate-200/80 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="applications-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">{t("admin.col.code")}</th>
                  <th className="px-4 py-3">{t("admin.col.name")}</th>
                  <th className="px-4 py-3 hidden md:table-cell">{t("admin.col.program")}</th>
                  <th className="px-4 py-3">{t("admin.col.status")}</th>
                  <th className="px-4 py-3 text-right">{t("admin.deficit")}</th>
                  <th className="px-4 py-3 text-right">{t("admin.col.grant")}</th>
                  <th className="px-4 py-3 text-right">{t("admin.col.actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loadingList ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></td></tr>
                ) : sorted.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">{t("admin.noresults")}</td></tr>
                ) : (
                  sorted.map((it) => (
                    <tr key={it.code_anonyme} className="hover:bg-slate-50 transition-colors" data-testid={`row-${it.code_anonyme}`}>
                      <td className="px-4 py-3 font-mono font-semibold text-primary">{it.code_anonyme}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{it.prenom} {it.nom}</div>
                        <div className="text-xs text-slate-400">{it.courriel}</div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-slate-600">
                        <div>{it.programme || "—"}</div>
                        <div className="text-xs text-slate-400">{it.cycle}</div>
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={it.statut_dossier} t={t} /></td>
                      <td className="px-4 py-3 text-right font-semibold" data-testid={`deficit-${it.code_anonyme}`}>
                        <span className={(it.deficit || 0) > 0 ? "text-red-600" : "text-emerald-600"}>
                          {(it.deficit || 0).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { style: "currency", currency: "CAD" })}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-700">
                        {(it.bourse_accordee || 0).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { style: "currency", currency: "CAD" })}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => openDetail(it.code_anonyme)} data-testid={`view-${it.code_anonyme}`}>
                            <Eye className="w-4 h-4 mr-1.5" /> {t("admin.view")}
                          </Button>
                          {view === "archives" && (
                            <Button size="sm" variant="ghost" onClick={() => doUnarchive(it.code_anonyme)} data-testid={`unarchive-${it.code_anonyme}`} className="text-secondary hover:text-secondary">
                              <RotateCcw className="w-4 h-4 mr-1.5" /> {t("admin.archive.unarchive")}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setDetail(null); Object.values(previews).forEach((u) => u && URL.revokeObjectURL(u)); setPreviews({}); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="detail-dialog">
          {!detail ? (
            <>
              <DialogTitle className="sr-only">{t("admin.view")}</DialogTitle>
              <div className="py-16 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3 font-display flex-wrap">
                  <span className="font-mono text-primary">{detail.code_anonyme}</span>
                  {detail.code_comite && (
                    <span className="font-mono text-xs font-semibold text-secondary bg-secondary/10 border border-secondary/20 rounded-full px-2.5 py-0.5" data-testid="detail-code-comite">
                      {t("admin.codeComite")} {detail.code_comite}
                    </span>
                  )}
                  <StatusBadge status={detail.statut_dossier} t={t} />
                </DialogTitle>
                <DialogDescription className="sr-only">
                  {t("admin.view")} — {detail.code_anonyme}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6">
                {/* Aperçu financier nominatif */}
                {(() => {
                  const revTotal = REVENUE_FIELDS.reduce((s, k) => s + (parseFloat(detail[k]) || 0), 0);
                  const depTotal = EXPENSE_FIELDS.reduce((s, k) => s + (parseFloat(detail[k]) || 0), 0);
                  const def = depTotal - revTotal;
                  const fmt = (n) => n.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { style: "currency", currency: "CAD" });
                  return (
                    <div className="grid grid-cols-3 gap-3" data-testid="financial-summary-card">
                      <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4">
                        <p className="text-[10px] uppercase tracking-wider text-emerald-600 font-semibold">{t("admin.revenus")}</p>
                        <p className="mt-1 font-display text-base sm:text-lg font-extrabold text-emerald-700">{fmt(revTotal)}</p>
                      </div>
                      <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("admin.depenses")}</p>
                        <p className="mt-1 font-display text-base sm:text-lg font-extrabold text-slate-800">{fmt(depTotal)}</p>
                      </div>
                      <div className={`rounded-xl border p-4 ${def > 0 ? "bg-red-50 border-red-100" : "bg-emerald-50 border-emerald-100"}`}>
                        <p className={`text-[10px] uppercase tracking-wider font-semibold ${def > 0 ? "text-red-600" : "text-emerald-600"}`}>{t("admin.deficitLabel")}</p>
                        <p className={`mt-1 font-display text-base sm:text-lg font-extrabold ${def > 0 ? "text-red-600" : "text-emerald-700"}`} data-testid="summary-deficit">{fmt(def)}</p>
                      </div>
                    </div>
                  );
                })()}

                {/* Admin section */}
                <section className="rounded-xl bg-secondary/5 border border-secondary/20 p-5" data-testid="admin-section">
                  <h3 className="text-sm font-bold text-secondary uppercase tracking-wide mb-4 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" /> {t("admin.section")}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-600">{t("admin.statutDossier")}</Label>
                      <Select value={detail.statut_dossier || "en_attente"} onValueChange={(v) => setDetail((d) => ({
                        ...d, statut_dossier: v,
                        bourse_accordee: v === "approuve" && !(parseFloat(d.bourse_accordee) > 0) ? grantDefault : d.bourse_accordee,
                      }))}>
                        <SelectTrigger className="bg-white" data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="en_attente">{t("status.en_attente")}</SelectItem>
                          <SelectItem value="approuve">{t("status.approuve")}</SelectItem>
                          <SelectItem value="rejete">{t("status.rejete")}</SelectItem>
                          <SelectItem value="incomplet">{t("status.incomplet")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-600">{t("admin.bourse")}</Label>
                      <Input type="number" min={0} value={detail.bourse_accordee ?? 0}
                        data-testid="detail-grant-input"
                        onChange={(e) => setDetail((d) => ({ ...d, bourse_accordee: e.target.value }))}
                        className="bg-white" />
                    </div>
                    <div className="flex items-center gap-2 sm:col-span-2">
                      <Checkbox id="versement" checked={!!detail.versement_effectue}
                        data-testid="detail-payment-checkbox"
                        onCheckedChange={(v) => setDetail((d) => ({ ...d, versement_effectue: !!v }))} />
                      <label htmlFor="versement" className="text-sm text-slate-700 cursor-pointer">{t("admin.versement")}</label>
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label className="text-xs font-semibold text-slate-600">{t("admin.notes")}</Label>
                      <Textarea value={detail.notes_admin || ""} rows={2}
                        data-testid="detail-notes-input"
                        onChange={(e) => setDetail((d) => ({ ...d, notes_admin: e.target.value }))}
                        className="bg-white resize-y" />
                    </div>
                  </div>
                  <Button onClick={saveAdmin} disabled={saving} data-testid="save-admin-btn" className="mt-4 bg-primary hover:bg-primary/90">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    {t("admin.save")}
                  </Button>
                </section>

                {/* Documents */}
                <section>
                  <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide border-l-4 border-primary pl-3 mb-3">{t("admin.docs")}</h3>
                  {[
                    { key: "facture_scolarite", ref: detail.facture_scolarite, dl: "download-facture-btn", label: t("admin.downloadFacture") },
                    { key: "contrat_ulaval", ref: detail.contrat_ulaval, dl: "download-contrat-btn", label: t("admin.downloadContrat") },
                  ].map((doc) => (
                    <div key={doc.key} className="mb-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-slate-700 mr-1">{doc.label}</span>
                        <Button size="sm" variant="outline" onClick={() => previewFile(doc.ref, doc.key)} disabled={!doc.ref} data-testid={`preview-${doc.key}`}>
                          <Eye className="w-4 h-4 mr-1.5" /> {previews[doc.key] ? t("admin.hidePreview") : t("admin.preview")}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => downloadFile(doc.ref)} disabled={!doc.ref} data-testid={doc.dl}>
                          <FileDown className="w-4 h-4 mr-1.5" /> PDF
                        </Button>
                      </div>
                      {previews[doc.key] && (
                        <iframe
                          title={doc.key}
                          src={previews[doc.key]}
                          data-testid={`iframe-${doc.key}`}
                          className="mt-3 w-full h-[480px] rounded-lg border border-slate-200 bg-slate-50"
                        />
                      )}
                    </div>
                  ))}
                </section>

                {/* Cover letter */}
                <section>
                  <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide border-l-4 border-primary pl-3 mb-3">{t("admin.letter")}</h3>
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="detail-letter">
                    {detail.lettre_presentation || "—"}
                  </div>
                  {detail.demarches_revenus && <p className="mt-3 text-sm text-slate-600"><span className="font-semibold">↑ Revenus:</span> {detail.demarches_revenus}</p>}
                  {detail.demarches_depenses && <p className="mt-2 text-sm text-slate-600"><span className="font-semibold">↓ Dépenses:</span> {detail.demarches_depenses}</p>}
                  {detail.commentaires && <p className="mt-2 text-sm text-slate-500 italic">{detail.commentaires}</p>}
                </section>

                {/* Applicant identity */}
                <section className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                  {[
                    ["Nom", `${detail.prenom || ""} ${detail.nom || ""}`],
                    ["NI", detail.ni],
                    ["Courriel", detail.courriel],
                    ["Téléphone", detail.telephone],
                    ["Faculté", detail.faculte === "autre" ? detail.faculte_autre : detail.faculte],
                    ["Programme", detail.programme],
                    ["Cycle", detail.cycle === "autre" ? detail.cycle_autre : detail.cycle],
                    ["Ville", detail.ville],
                    ["Soumission", detail.date_soumission],
                    [t("admin.codeComite"), detail.code_comite],
                    [t("admin.heuresTravail"), detail.heures_travail_semaine != null ? `${detail.heures_travail_semaine} h` : ""],
                    [t("admin.resteScolarite"), detail.reste_scolarite != null ? Number(detail.reste_scolarite).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { style: "currency", currency: "CAD" }) : ""],
                  ].map(([k, v]) => (
                    <div key={k} className="rounded-lg bg-white border border-slate-100 p-3">
                      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{k}</p>
                      <p className="text-slate-800 font-medium truncate">{v || "—"}</p>
                    </div>
                  ))}
                </section>

                {/* Historique des décisions */}
                <section>
                  <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide border-l-4 border-primary pl-3 mb-3">{t("admin.history")}</h3>
                  {(detail.status_history && detail.status_history.length > 0) ? (
                    <ol className="space-y-2" data-testid="status-history">
                      {[...detail.status_history].reverse().map((h, i) => (
                        <li key={`${h.at || "h"}-${i}`} className="flex items-center justify-between gap-3 text-sm bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                          <span className="flex items-center gap-2">
                            <StatusBadge status={h.to} t={t} />
                            <span className="text-xs text-slate-400">{t("admin.history.by")} {h.by || "—"}</span>
                          </span>
                          <span className="text-xs text-slate-500">{h.at ? new Date(h.at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA") : ""}</span>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="text-sm text-slate-400">{t("admin.history.empty")}</p>
                  )}
                </section>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={confirmArchive} onOpenChange={setConfirmArchive}>
        <DialogContent data-testid="archive-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Archive className="w-5 h-5 text-secondary" /> {t("admin.archive.confirmTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("admin.archive.confirmDesc", { session: t(`admin.archive.session.${archiveSession}`), year: archiveYear })}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={() => setConfirmArchive(false)} data-testid="archive-cancel-btn">{t("admin.archive.cancel")}</Button>
            <Button onClick={doArchive} disabled={archiving} data-testid="archive-confirm-btn" className="bg-secondary hover:bg-secondary/90 text-white">
              {archiving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Archive className="w-4 h-4 mr-2" />}
              {t("admin.archive.confirmBtn")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
